import { getImageSources } from "@dotcom/dato";
import { convertFocalPointToCssObjectPosition } from "@dotcom/ui";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  Alignments,
  AspectRatios,
  Breakpoints,
  Button,
  ButtonVariants,
  Card,
  createCSSTransition,
  CrossAxisAlignments,
  Heading,
  Icons,
  Image,
  Orientations,
  Paragraph,
  Picture,
  Row,
  Spacer,
  useMediaQuery,
} from "@polestar/component-warehouse-react";
import { Link } from "@remix-run/react";

import { type ParsedImage } from "~/common/types";

const StyledCard = styled(Card)`
  width: 100%;
`;

const StyledParagraph = styled(Paragraph)(({ theme }) => {
  return css`
    transition: ${createCSSTransition(
      "color",
      theme.card.states.enabled.motionEase,
      theme.card.states.enabled.motionDuration
    )};
  `;
});

interface Props {
  readonly locale: string;
  readonly slug?: string;
  readonly headline?: string;
  readonly ingress?: string;
  readonly date?: string;
  readonly readMoreLabel?: string;
  readonly imageMobile?: ParsedImage;
  readonly imageTablet?: ParsedImage;
  readonly imageDesktop?: ParsedImage;
}

const RelatedNewsArticle = ({
  locale,
  slug,
  headline,
  ingress,
  date,
  readMoreLabel,
  imageMobile,
  imageTablet,
  imageDesktop,
}: Props) => {
  const { queryMatches } = useMediaQuery();

  const imageSources = getImageSources({
    mobile: {
      url: imageMobile ? imageMobile.url : imageDesktop?.url,
      imageWidth: 620,
      breakpoint: Breakpoints.mobile,
    },
    tablet: {
      url: imageTablet ? imageTablet.url : imageDesktop?.url,
      imageWidth: 284,
      breakpoint: Breakpoints.tablet,
    },
    desktop: {
      url: imageDesktop?.url,
      imageWidth: 334,
      breakpoint: Breakpoints.desktop,
    },
    desktopWide: {
      url: imageDesktop?.url,
      imageWidth: 512,
      breakpoint: Breakpoints.desktopWide,
    },
  });

  return (
    <Link
      to={`/${locale}/news/${slug ?? ""}`}
      prefetch="intent"
      style={{ textDecoration: "none" }}
    >
      <StyledCard
        orientation={
          queryMatches.tablet ? Orientations.horizontal : Orientations.vertical
        }
        mediaAspectRatio={AspectRatios.wide}
        media={
          <Picture stretch>
            {imageSources.sources.map(({ media, srcSet }) => {
              return <source key={media} srcSet={srcSet} media={media} />;
            })}
            <Image
              stretch
              src={imageSources.source}
              alt={imageDesktop?.alt ?? ""}
              objectFit="cover"
              objectPosition={{
                mobile: convertFocalPointToCssObjectPosition(
                  imageMobile?.focalPoint
                ),
                tablet: convertFocalPointToCssObjectPosition(
                  imageTablet?.focalPoint
                ),
                desktop: convertFocalPointToCssObjectPosition(
                  imageDesktop?.focalPoint
                ),
                desktopWide: convertFocalPointToCssObjectPosition(
                  imageDesktop?.focalPoint
                ),
              }}
            />
          </Picture>
        }
        title={
          <>
            <Paragraph as="time">{date}</Paragraph>
            <Spacer />
            <Heading level={2}>{headline}</Heading>
          </>
        }
        body={<StyledParagraph>{ingress}</StyledParagraph>}
        actions={
          <Row crossAxisAlignment={CrossAxisAlignments.center}>
            <Button
              variant={ButtonVariants.text}
              icon={Icons.arrowRight}
              iconPosition={Alignments.right}
            >
              {readMoreLabel}
            </Button>
          </Row>
        }
      />
    </Link>
  );
};

export default RelatedNewsArticle;
